// src/hooks/useTrainingSessions.js
import { useState, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders.js';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export const useTrainingSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create training session (analyze + generate)
  const createSession = useCallback(async ({
    assistantId,
    sessionName,
    sessionDescription,
    trainingMode,
    conversationIds
  }) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${BASE_URL}/api/training/sessions/create`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          assistantId,
          sessionName,
          sessionDescription,
          trainingMode,
          conversationIds
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create training session');
      }

      const session = await response.json();
      return session;
    } catch (err) {
      setError(err.message);
      console.error('Error creating training session:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all sessions
  const fetchSessions = useCallback(async ({
    assistantId = null,
    status = null,
    limit = 20,
    offset = 0
  } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      const params = new URLSearchParams();
      if (assistantId) params.append('assistant_id', assistantId);
      if (status) params.append('status', status);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await fetch(`${BASE_URL}/api/training/sessions?${params}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch training sessions');
      }

      const data = await response.json();
      setSessions(data.sessions);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching training sessions:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get session by ID
  const getSessionById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/sessions/${id}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch training session');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      console.error('Error fetching training session:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Approve session (creates new assistant)
  const approveSession = useCallback(async (id, reviewNotes = null) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/sessions/${id}/approve`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ review_notes: reviewNotes })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve training session');
      }

      const result = await response.json();

      // Update local state
      setSessions(prev =>
        prev.map(s => s.id === id ? result.session : s)
      );

      return result; // { session, newAssistant }
    } catch (err) {
      setError(err.message);
      console.error('Error approving training session:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reject session
  const rejectSession = useCallback(async (id, reviewNotes) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/training/sessions/${id}/reject`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ review_notes: reviewNotes })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reject training session');
      }

      const updatedSession = await response.json();

      // Update local state
      setSessions(prev =>
        prev.map(s => s.id === id ? updatedSession : s)
      );

      return updatedSession;
    } catch (err) {
      setError(err.message);
      console.error('Error rejecting training session:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sessions,
    loading,
    error,
    createSession,
    fetchSessions,
    getSessionById,
    approveSession,
    rejectSession
  };
};
